import { GmailAttachmentMetadataSchema } from './gmailDraft';
/**
 * Connexions de comptes externes (Réglages › Intégrations) : Google Drive, Microsoft et Gmail.
 *
 * Contrat partagé entre `apps/backend/routes/workspace-connections*.routes.ts`,
 * `apps/backend/services/workspace-connections/`, et le front `legalworkspace`
 * (`sharepoint-integration/`, `google-drive-integration/`). Jusqu'au 2026-09-04
 * le fournisseur était une chaîne libre des deux côtés et le CHECK SQL la seule
 * source de vérité ; ce module la fixe en un seul endroit.
 *
 * Hub : `docs/features/workspace-connections/`.
 */

import { z } from 'zod';

/** Valeurs du CHECK `workspace_connections.provider` après la migration Google 211. */
export const WORKSPACE_CONNECTION_PROVIDERS = ['google', 'microsoft'] as const;
export type WorkspaceConnectionProvider = (typeof WORKSPACE_CONNECTION_PROVIDERS)[number];
export const WorkspaceConnectionProviderSchema = z.enum(WORKSPACE_CONNECTION_PROVIDERS);

/**
 * Périmètres Microsoft, chaînes EXACTES envoyées à `login.microsoftonline.com`
 * (paramètre `scope` de l'autorisation et de l'échange de jeton).
 *
 * WHY deux listes : le courrier est un consentement INCRÉMENTAL sur la connexion
 * fichiers existante (spec 2026-09-04, décision D2). Une connexion peut donc ne
 * porter que les périmètres fichiers ; `hasMicrosoftMailScopes` fait la différence.
 * `Mail.Send` est volontairement absent : l'envoi reste le clic de l'avocat.
 */
export const MICROSOFT_FILE_SCOPES = ['User.Read', 'Files.Read.All', 'Sites.Read.All', 'offline_access'] as const;
export const MICROSOFT_MAIL_SCOPES = ['Mail.ReadWrite'] as const;
export const MICROSOFT_ALL_SCOPES = [...MICROSOFT_FILE_SCOPES, ...MICROSOFT_MAIL_SCOPES] as const;

/** Périmètre Google Drive demandé par `google-drive-integration/googleDriveConfig.ts`. */
export const GOOGLE_DRIVE_SCOPES = ['https://www.googleapis.com/auth/drive.readonly'] as const;

/** Ce que l'avocat cherche à activer en ouvrant la fenêtre Microsoft. */
export const MicrosoftConsentIntentSchema = z.enum(['files', 'mail']);
export type MicrosoftConsentIntent = z.infer<typeof MicrosoftConsentIntentSchema>;

/** Périmètres à demander pour un intent donné. */
export function microsoftScopesForIntent(intent: MicrosoftConsentIntent): readonly string[] {
  return intent === 'mail' ? MICROSOFT_ALL_SCOPES : MICROSOFT_FILE_SCOPES;
}

/**
 * Périmètres à demander pour un intent quand la connexion en porte déjà :
 * ceux de l'intent, puis ceux déjà accordés qui n'y figurent pas.
 *
 * WHY cumulatif : `granted_scopes` est REMPLACÉ par ce que Microsoft renvoie à
 * chaque échange de code. Demander les seuls périmètres de l'intent retirerait
 * donc ceux d'un consentement précédent (consentir `files` après `mail` ferait
 * perdre `Mail.ReadWrite`, et les outils courrier avec).
 * Comparaison insensible à la casse, comme `hasMicrosoftMailScopes`.
 */
export function cumulativeMicrosoftScopes(
  intent: MicrosoftConsentIntent,
  alreadyGranted: readonly string[],
): string[] {
  const scopes: string[] = [];
  const seen = new Set<string>();
  for (const scope of [...microsoftScopesForIntent(intent), ...alreadyGranted]) {
    const key = scope.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    scopes.push(scope);
  }
  return scopes;
}

/** Corps de `POST /api/workspace-connections/microsoft/callback`. */
export const MicrosoftCallbackBodySchema = z.object({
  code: z.string().min(1),
  redirect_uri: z.string().url().optional(),
  intent: MicrosoftConsentIntentSchema.default('files'),
});
export type MicrosoftCallbackBody = z.infer<typeof MicrosoftCallbackBodySchema>;

/**
 * Forme publique d'une connexion : ce que `GET /api/workspace-connections` et le
 * callback renvoient au navigateur. Jamais de jeton, chiffré ou non.
 */
export const WorkspaceConnectionPublicSchema = z.object({
  provider: WorkspaceConnectionProviderSchema,
  provider_email: z.string().nullable(),
  provider_name: z.string().nullable(),
  provider_picture: z.string().nullable(),
  /** Périmètres réellement accordés (champ `scope` de la réponse jeton, sinon ceux demandés). Jamais absent. */
  granted_scopes: z.array(z.string()),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type WorkspaceConnectionPublic = z.infer<typeof WorkspaceConnectionPublicSchema>;

/** Réponse de `GET /api/workspace-connections`. */
export const WorkspaceConnectionsListResponseSchema = z.object({
  success: z.literal(true),
  connections: z.array(WorkspaceConnectionPublicSchema),
});
export type WorkspaceConnectionsListResponse = z.infer<typeof WorkspaceConnectionsListResponseSchema>;

/**
 * Détail porté par l'erreur HTTP 409 du callback quand le tenant de l'avocat
 * exige le consentement d'un administrateur (AADSTS65001 / `consent_required`).
 *
 * Le corps suit l'enveloppe d'erreur STANDARD du backend
 * `{ error: 'admin_consent_required', message, details }` : c'est la forme que
 * `apiClient.buildApiError` (legalworkspace) conserve dans l'erreur levée
 * (`statusCode`, `message`, `details`), donc le front lit
 * `details.admin_consent_url` sans contourner `apiClient`.
 */
export const MicrosoftAdminConsentRequiredDetailsSchema = z.object({
  admin_consent_url: z.string().url(),
});
export type MicrosoftAdminConsentRequiredDetails = z.infer<typeof MicrosoftAdminConsentRequiredDetailsSchema>;

export const MicrosoftAdminConsentRequiredResponseSchema = z.object({
  error: z.literal('admin_consent_required'),
  message: z.string(),
  details: MicrosoftAdminConsentRequiredDetailsSchema,
});
export type MicrosoftAdminConsentRequiredResponse = z.infer<typeof MicrosoftAdminConsentRequiredResponseSchema>;

/**
 * Vrai si la connexion couvre TOUS les périmètres courrier. Comparaison exacte,
 * insensible à la casse : Microsoft renvoie parfois les périmètres en casse
 * différente de la demande (`mail.readwrite`).
 */
export function hasMicrosoftMailScopes(grantedScopes: readonly string[]): boolean {
  const granted = new Set(grantedScopes.map((scope) => scope.toLowerCase()));
  return MICROSOFT_MAIL_SCOPES.every((scope) => granted.has(scope.toLowerCase()));
}

/**
 * État du connecteur courrier Microsoft pour l'utilisateur courant, tel que
 * `GET /api/workspace-connections/microsoft/mailbox-status` le renvoie. Du plus
 * fermé au plus ouvert :
 * - `not_connected` : aucune connexion Microsoft — proposer « Connecter » (intent `mail`) ;
 * - `files_only` : connexion sans `Mail.ReadWrite` — proposer « Activer le courrier » (intent `mail`) ;
 * - `ready` : connexion avec périmètre courrier — les outils `mailbox_*` sont exposés à l'agent.
 *
 * Trois états et non un booléen : le volet Outlook et Réglages › Intégrations
 * n'affichent pas la même invitation selon qu'il manque la connexion entière
 * ou seulement le périmètre courrier.
 */
export const MAILBOX_CONNECTOR_STATES = ['not_connected', 'files_only', 'ready'] as const;
export const MailboxConnectorStateSchema = z.enum(MAILBOX_CONNECTOR_STATES);
export type MailboxConnectorState = z.infer<typeof MailboxConnectorStateSchema>;

/** Réponse de `GET /api/workspace-connections/microsoft/mailbox-status`. */
export const MailboxConnectorStatusResponseSchema = z.object({
  success: z.literal(true),
  state: MailboxConnectorStateSchema,
  /** Adresse du compte Microsoft connecté (`null` sans connexion). */
  provider_email: z.string().nullable(),
});
export type MailboxConnectorStatusResponse = z.infer<typeof MailboxConnectorStatusResponseSchema>;

/**
 * Query de `GET /api/workspace-connections/microsoft/authorize-url`. Le serveur
 * construit l'URL de consentement (il détient `MICROSOFT_CLIENT_ID`) ; le client
 * fournit l'URI de retour enregistrée dans Entra pour SA surface et un `state`
 * qu'il revérifie au retour (anti-CSRF).
 */
export const MicrosoftAuthorizeUrlQuerySchema = z.object({
  intent: MicrosoftConsentIntentSchema.default('files'),
  redirect_uri: z.string().url(),
  state: z.string().min(1).max(256).optional(),
});
export type MicrosoftAuthorizeUrlQuery = z.infer<typeof MicrosoftAuthorizeUrlQuerySchema>;

export const MicrosoftAuthorizeUrlResponseSchema = z.object({
  success: z.literal(true),
  authorize_url: z.string().url(),
});
export type MicrosoftAuthorizeUrlResponse = z.infer<typeof MicrosoftAuthorizeUrlResponseSchema>;

/**
 * Gmail needs read access plus draft composition; Google has no draft-only scope.
 * Hector never exposes a send endpoint even though compose permits sending at Google.
 * Source: https://developers.google.com/workspace/gmail/api/auth/scopes (verified 2026-10-14).
 */
export const GMAIL_READ_SCOPES = ['https://www.googleapis.com/auth/gmail.readonly'] as const;
export const GMAIL_COMPOSE_SCOPES = ['https://www.googleapis.com/auth/gmail.compose'] as const;
export const GMAIL_MAIL_SCOPES = [...GMAIL_READ_SCOPES, ...GMAIL_COMPOSE_SCOPES] as const;

export const GmailAuthorizeBodySchema = z.object({
  expectedEmail: z.string().email().max(320).optional(),
}).strict();
export const GmailAuthorizeResponseSchema = z.object({
  success: z.literal(true),
  authorization_url: z.string().url(),
  authorization_callback_uri: z.string().url(),
  state: z.string().min(1),
});
export type GmailAuthorizeResponse = z.infer<typeof GmailAuthorizeResponseSchema>;
export const GmailCompleteBodySchema = z.object({
  state: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  code: z.string().min(1).max(4096),
}).strict();
export const GmailConnectorStatusResponseSchema = z.object({
  success: z.literal(true),
  surface: z.literal('gmail'),
  protocol_version: z.literal(1),
  // not_configured: this backend has no Gmail OAuth client, so no connection can be offered.
  state: z.enum(['not_configured', 'not_connected', 'ready', 'unavailable']),
  provider_email: z.string().nullable(),
});
export type GmailConnectorStatusResponse = z.infer<typeof GmailConnectorStatusResponseSchema>;
/** `error` codes of Gmail connection failures. Clients translate the code; the `message` is for logs. */
export const GMAIL_CONNECTION_ERROR_CODES = ['gmail_not_connected', 'gmail_scope_missing', 'gmail_oauth_invalid', 'gmail_unavailable', 'gmail_account_mismatch'] as const;
export type GmailConnectionErrorCode = typeof GMAIL_CONNECTION_ERROR_CODES[number];

/** Mailbox binding is mandatory for every Gmail file read, including manual imports. */
export const GmailMailboxAccountHeaderSchema = z.string().trim().email().max(320).transform((value) => value.toLowerCase());
export const GmailMessageReadParamsSchema = z.object({
  messageId: z.string().min(1).max(1024).regex(/^[A-Za-z0-9_-]+$/),
}).strict();
export const GmailAttachmentReadParamsSchema = GmailMessageReadParamsSchema.extend({
  // MIME part identifiers can be nested (e.g. 1.2); these are not Google attachment IDs.
  partId: z.string().min(1).max(1024).regex(/^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/),
}).strict();
export const GmailMessageMetadataResponseSchema = z.object({
  attachments: z.array(GmailAttachmentMetadataSchema),
}).strict();
export type GmailMessageMetadataResponse = z.infer<typeof GmailMessageMetadataResponseSchema>;

export * from './gmailSelection';

export * from './gmailDraft';
export * from './gmailFiling';

export * from './gmailMessage';

export * from './gmailIngestion.js';

export * from './googleConnection';
export * from './mailboxFiling';
